import produce from 'immer';

import {BatchSchema} from 'constants/SampleConstants';
import {
  LastSyncedUpstreamValue,
  PrimarySample,
  StructuredTable,
  StructuredTableColumn,
  StructuredTableRow,
  StructuredTableRowUpdater,
  StructuredTableRowUpdaterModel,
} from 'notebook/structured-tables/StructuredTableConstants';
import fetchAllSamples from 'notebook/structured-tables/util/fetchAllSamples';
import getLastSyncedUpstreamValueForColumn from 'notebook/structured-tables/util/getLastSyncedUpstreamValueForColumn';
import {resolveRowDelegateColumn} from 'notebook/structured-tables/util/resolveRowDelegateColumn';
import {cellHasUnsubmittedChanges} from 'notebook/structured-tables/util/rowHasUnsubmittedChanges';
import {Cell} from 'notebook/tables/constants/CellConstants';
import {TableColumnType} from 'notebook/tables/constants/TableColumnConstants';
import {EvaluatedCell} from 'notebook/tables/excess/EvaluationConstants';
import TableUtil from 'notebook/tables/TableUtil';
import {areTypedValuesEquivalent} from 'notebook/tables/typed-values/DataTypes';
import {FieldType} from 'schemas/fields/SchemaFieldConstants';
import {Schema} from 'schemas/SchemaConstants';
import {omit} from 'util/omitKey';

// Keep in sync with LAST_SYNCED_UPSTREAM_VALUES_SCHEMA in benchling/notebook/structured_tables/validation.py
// and SUPPORTED_COLUMN_TYPES in benchling/notebook/structured_tables/resync/constants.py
export const SUPPORTED_COLUMN_TYPES = [
  TableColumnType.TAG_VALUE,
  TableColumnType.ALIAS,
  TableColumnType.UNIT_SELECTOR,
  TableColumnType.UNIT_REFERENCE,
  TableColumnType.MEASUREMENT_AMOUNT,
  TableColumnType.INGREDIENT_CATALOG_ID,
  TableColumnType.INGREDIENT_COMPONENT_LOT_TEXT,
  TableColumnType.EXISTING_BIOENTITY,
  TableColumnType.CONTAINERS,
  TableColumnType.INGREDIENT_NOTES,
  TableColumnType.PROCESS_OUTPUT,
  TableColumnType.PROCESS_TASK,
  TableColumnType.PROCESS_TASK_ASSIGNEE,
  TableColumnType.PROCESS_TASK_SCHEDULED_ON_DATE,
  TableColumnType.PROCESS_TASK_STATUS,
  TableColumnType.CUSTOM,
  TableColumnType.STUDIES,
  TableColumnType.PROCEDURE_CONDITION_REPLICATES,
  TableColumnType.STRING,
  TableColumnType.OPTION_SELECTOR,
];

// Column types to preserve last synced upstream values on refresh. These are column types that have no
// external source of truth and will only be updated on table submission.
// On refresh, any unsubmitted updates to the cells will be wiped away.
// Keep in sync with COLUMN_TYPES_PRESERVE_LAST_SYNCED_UPSTREAM_VALUES in benchling/notebook/structured_tables/resync/constants.py
export const COLUMN_TYPES_PRESERVE_LAST_SYNCED_UPSTREAM_VALUES = [
  TableColumnType.CUSTOM,
  TableColumnType.PROCEDURE_CONDITION_REPLICATES,
  TableColumnType.OPTION_SELECTOR,
];

/**
 * Calculate and insert the appropriate lastSyncedUpstreamValues for the updatedRow.  If the value
 * for a column hasn't changed, then keep the old submittedAt time.
 *
 * @param columns - the columns in the table
 * @param originalRow - the originalRow with the old values of lastSyncedUpstreamValue
 * @param updatedRow - the new row which does not yet have lastSyncedUpstreamValues populated
 */
// Keep in sync with update_last_synced_upstream_values in benchling/notebook/structured_tables/managers/submission_manager.py
export function updateLastSyncedUpstreamValues<TRowMetadata>(
  columns: Array<StructuredTableColumn>,
  originalRow: StructuredTableRow<TRowMetadata> | null,
  updatedRow: StructuredTableRow<TRowMetadata>
): StructuredTableRow<TRowMetadata> {
  const lastSyncedUpstreamValues = originalRow?.lastSyncedUpstreamValues ?? [];
  const updatedSubmittedAt = new Date().toISOString();
  const updatedLastSyncedUpstreamValues: Array<LastSyncedUpstreamValue> = columns
    .map((column, columnIndex) => {
      if (!SUPPORTED_COLUMN_TYPES.includes(column.columnType)) {
        return null;
      }
      const originalLastSyncedValue = getLastSyncedUpstreamValueForColumn(lastSyncedUpstreamValues, column);
      const updatedTypedValue = updatedRow.cells[columnIndex].typedValue ?? null;
      const originalTypedValue = originalLastSyncedValue?.value ?? null;
      if (originalRow != null && areTypedValuesEquivalent(originalTypedValue, updatedTypedValue)) {
        return originalLastSyncedValue;
      } else if (
        originalRow != null &&
        updatedTypedValue?.type === FieldType.TEXT &&
        originalRow.cells[columnIndex].link
      ) {
        // If the plaintext typed value has changed outside of the editor context, clear its link
        updatedRow.cells[columnIndex].link = null;
      }
      return {
        columnType: column.columnType,
        columnGroup: column.columnGroup ?? null,
        fieldId: column.schemaFieldApiId ?? null,
        columnProvenanceUuid: column.provenanceUuid,
        value: updatedTypedValue,
        submittedAt: updatedSubmittedAt,
      };
    })
    .filter((x): x is LastSyncedUpstreamValue => x != null);
  return {
    ...updatedRow,
    lastSyncedUpstreamValues: updatedLastSyncedUpstreamValues,
  };
}

export interface StructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata> {
  samplesByRowId: {[rowId: string]: Array<PrimarySample>};
  rowUpdaters: Array<StructuredTableRowUpdater<StructuredTableRowUpdaterModel, TRowMetadata>>;
  structuredTable: StructuredTable<TTableMetadata, TRowMetadata>;
  /** Used by refreshEntitiesInStructuredTableUsingRowUpdater for unsubmitted-change detection. */
  computedTable?: Array<Array<EvaluatedCell>>;
}

/** Fetches live upstream values per row. Does not normalize merged cells. */
function refreshRowsWithRowUpdater<TTableMetadata, TRowMetadata>({
  samplesByRowId,
  rowUpdaters,
  structuredTable,
}: StructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata>): Promise<
  Array<StructuredTableRow<TRowMetadata>>
> {
  // liveDataOnly updaters only power the live-data / staleness-indicator path
  // (fetchStructuredTableLiveData). Exclude them here so their matched samples never reach
  // updateRow and never trigger the LSUV recompute below, which would otherwise clobber the LSUV
  // baseline for columns whose LSUVs are set on submit, not by this refresh flow.
  const rowRefreshUpdaters = rowUpdaters.filter((rowUpdater) => !rowUpdater.liveDataOnly);

  return Promise.all(
    structuredTable.rows.map(async (originalRow) => {
      const {rowId} = originalRow;
      if (rowId == null || samplesByRowId[rowId] == null) {
        return originalRow;
      }
      const updatedSamples = await fetchAllSamples(samplesByRowId[rowId], rowRefreshUpdaters);

      let updatedRow = originalRow;
      for (const {updates, columnGroup} of updatedSamples) {
        let newRow = updatedRow;
        for (const {rowUpdater, updatedModel} of updates) {
          newRow = rowUpdater.updateRow(updatedModel, structuredTable.columns, newRow, columnGroup);
        }
        const resolvedColumns = structuredTable.columns.map((column) =>
          resolveRowDelegateColumn(column, newRow, structuredTable.delegatedColumns)
        );
        updatedRow = {
          ...updateLastSyncedUpstreamValues(resolvedColumns, updatedRow, newRow),
          lastSyncedArchivePurpose: newRow.lastSyncedArchivePurpose,
        };
      }
      return updatedRow;
    })
  );
}

/**
 * Updates a StructuredTable by using the given StructuredTableRowUpdater to fetch any samples
 * that it knows how to fetch and then update the given row for that sample.  This function is called
 * during the RefreshTableData flow and allows various StructuredTableAdapters to specify how they
 * want to update entries that aren't just the standard Bioentities or Storables and their tag values.
 */
export async function updateStructuredTableUsingRowUpdater<TTableMetadata, TRowMetadata>(
  args: StructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata>
): Promise<StructuredTable<TTableMetadata, TRowMetadata>> {
  const updatedRows = await refreshRowsWithRowUpdater(args);
  return {
    ...args.structuredTable,
    rows: normalizeMergeCellRanges(updatedRows),
  };
}

function mergeRangeHasConflictingCells<TRowMetadata>(
  rows: Array<StructuredTableRow<TRowMetadata>>,
  startRowIdx: number,
  startColIdx: number,
  rowSpan: number,
  colSpan: number,
  {onlyFinalizedRows}: {onlyFinalizedRows: boolean}
): boolean {
  const primaryCell = rows[startRowIdx].cells[startColIdx];
  for (let rowIdx = startRowIdx; rowIdx < startRowIdx + rowSpan; rowIdx++) {
    if (onlyFinalizedRows && !rows[rowIdx].isFinalized) {
      continue;
    }
    for (let colIdx = startColIdx; colIdx < startColIdx + colSpan; colIdx++) {
      if (rowIdx === startRowIdx && colIdx === startColIdx) {
        continue;
      }
      const cell = rows[rowIdx].cells[colIdx];
      if (
        cell.text !== primaryCell.text ||
        !areTypedValuesEquivalent(cell.typedValue ?? null, primaryCell.typedValue ?? null)
      ) {
        return true;
      }
    }
  }
  return false;
}

/**
 * Normalize the results of updating the table via the row updater with any merged cell ranges on the table.
 * The row updater will write live data to cells, ignoring merge cell ranges. This method inspects each merge
 * cell range and takes one of two actions based on whether data in each cell in the range matches the
 * "primary" (top left) cell...
 * 1. (Data matches): Replace non-primary cells with an empty cell
 * 2. (Data does not match): Remove the merge so that all cells will be visible
 *
 * When `liveRows` is passed (entities-only refresh), conflict detection uses each row's live upstream
 * cell in the range so merges break when entities diverge even if a preserved formula cell was restored
 * on the primary.
 *
 * Note: This method is compatible with 2D merge ranges, even though we only support single-column ranges in
 * structured tables.
 */
// Keep in sync with normalize_merge_cell_ranges in benchling/notebook/structured_tables/managers/merged_cell_manager.py
export function normalizeMergeCellRanges<TRowMetadata>(
  rows: Array<StructuredTableRow<TRowMetadata>>,
  liveRows?: Array<StructuredTableRow<TRowMetadata>>
): Array<StructuredTableRow<TRowMetadata>> {
  const rowsForValueComparison = liveRows ?? rows;

  return produce(rows, (draftRows) => {
    draftRows.forEach((row, rowIdx) => {
      row.cells.forEach((cell, colIdx) => {
        if (cell.mergeRowSpan == null && cell.mergeColSpan == null) {
          return;
        }

        const rowSpan = cell.mergeRowSpan ?? 1;
        const colSpan = cell.mergeColSpan ?? 1;
        const rowsInRange = draftRows.slice(rowIdx, rowIdx + rowSpan);

        const hasArchivedRow = rowsInRange.some(
          (rangeRow) => rangeRow.isFinalized && rangeRow.lastSyncedArchivePurpose != null
        );
        // Standard refresh ignores non-finalized rows; entities refresh compares every row's live value.
        const hasConflictingValues = mergeRangeHasConflictingCells(
          rowsForValueComparison,
          rowIdx,
          colIdx,
          rowSpan,
          colSpan,
          {onlyFinalizedRows: liveRows == null}
        );

        if (hasArchivedRow || hasConflictingValues) {
          draftRows[rowIdx].cells[colIdx] = omit(
            draftRows[rowIdx].cells[colIdx],
            'mergeRowSpan',
            'mergeColSpan'
          );
          return;
        }

        for (let i = rowIdx; i < rowIdx + rowSpan; i++) {
          for (let j = colIdx; j < colIdx + colSpan; j++) {
            if (i !== rowIdx || j !== colIdx) {
              draftRows[i].cells[j] = {text: ''};
            }
          }
        }
      });
    });
  });
}

function pickLiveOrPreservedCells<TRowMetadata>(
  resolvedColumns: Array<StructuredTableColumn>,
  originalRow: StructuredTableRow<TRowMetadata>,
  liveCells: Array<Cell>,
  computedRow: Array<EvaluatedCell>,
  schemas: Array<Schema | BatchSchema>
): Array<Cell> {
  return resolvedColumns.map((column, colIdx) => {
    const originalCell = originalRow.cells[colIdx];

    if (TableUtil.isCellFormula(originalCell)) {
      return originalCell;
    }

    if (!originalRow.isFinalized) {
      return liveCells[colIdx];
    }

    const lastSyncedUpstreamValues = originalRow.lastSyncedUpstreamValues;
    if (lastSyncedUpstreamValues === undefined) {
      return liveCells[colIdx];
    }
    // eslint-disable-next-line benchlint/no-eqeqeq-null -- null means the row was never synced; preserve edits
    if (lastSyncedUpstreamValues === null) {
      return originalCell;
    }

    if (cellHasUnsubmittedChanges(computedRow[colIdx], lastSyncedUpstreamValues, column, schemas)) {
      return originalCell;
    }

    return liveCells[colIdx];
  });
}

function updateLsuvsAfterEntitiesRefresh<TRowMetadata>(
  originalRow: StructuredTableRow<TRowMetadata>,
  refreshedRow: StructuredTableRow<TRowMetadata>,
  resolvedColumns: Array<StructuredTableColumn>,
  computedRow: Array<EvaluatedCell>
): Array<LastSyncedUpstreamValue> | null | undefined {
  if (originalRow.isFinalized) {
    return refreshedRow.lastSyncedUpstreamValues;
  }

  const originalLsuvs = originalRow.lastSyncedUpstreamValues ?? [];
  const refreshedLsuvs = refreshedRow.lastSyncedUpstreamValues ?? [];
  const updatedLsuvs = resolvedColumns
    .map((column, colIdx) => {
      const refreshedLSUV = getLastSyncedUpstreamValueForColumn(refreshedLsuvs, column);
      // We specifically preserve LSUVs for formula cells when the evaluated result differs from live.
      // We should keep the out-of-date indicators if a formula is still producing a different value from live.
      if (
        TableUtil.isCellFormula(originalRow.cells[colIdx]) &&
        !areTypedValuesEquivalent(refreshedLSUV?.value ?? null, computedRow[colIdx]?.value ?? null)
      ) {
        return getLastSyncedUpstreamValueForColumn(originalLsuvs, column);
      }
      return refreshedLSUV;
    })
    .filter((x): x is LastSyncedUpstreamValue => x != null);

  if (updatedLsuvs.length === 0) {
    return originalRow.lastSyncedUpstreamValues;
  }
  return updatedLsuvs;
}

/**
 * A partially non-destructive alternative to updateStructuredTableUsingRowUpdater.
 *
 * Runs the same row-updater + LSUV pipeline, then merges live values back onto the prior row on a
 * per-cell basis (preserving formulas and unsubmitted edits on submitted rows). Merged cells are
 * finalized last so each row's live upstream value decides whether the merge is kept.
 *
 * On unsubmitted rows, every non-formula cell advances to its live value; formula cells and their
 * LSUVs are preserved when the evaluated result differs from live.
 *
 * On submitted rows, a cell advances only when it has no formula and no unsubmitted edit (same rules
 * as stale-cell indicators). LSUVs always reflect the live upstream state.
 */
export type EntitiesStructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata> =
  StructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata> & {
    computedTable: Array<Array<EvaluatedCell>>;
  };

export async function refreshEntitiesInStructuredTableUsingRowUpdater<TTableMetadata, TRowMetadata>(
  args: EntitiesStructuredTableRowUpdaterRefreshArgs<TTableMetadata, TRowMetadata>
): Promise<StructuredTable<TTableMetadata, TRowMetadata>> {
  const {samplesByRowId, structuredTable, computedTable, rowUpdaters} = args;
  const schemas = structuredTable.schemas ?? [];

  const liveRows = await refreshRowsWithRowUpdater({samplesByRowId, rowUpdaters, structuredTable});

  const originalRowsByRowId = new Map(
    structuredTable.rows.flatMap((row) => (row.rowId != null ? [[row.rowId, row] as const] : []))
  );

  const rowsAfterCellPolicy = liveRows.map((liveRow, rowIndex) => {
    if (liveRow.rowId == null || samplesByRowId[liveRow.rowId] == null) {
      return liveRow;
    }
    const originalRow = originalRowsByRowId.get(liveRow.rowId);
    if (originalRow == null) {
      return liveRow;
    }

    const resolvedColumns = structuredTable.columns.map((column) =>
      resolveRowDelegateColumn(column, liveRow, structuredTable.delegatedColumns)
    );

    return {
      ...liveRow,
      cells: pickLiveOrPreservedCells(
        resolvedColumns,
        originalRow,
        liveRow.cells,
        computedTable[rowIndex],
        schemas
      ),
      lastSyncedUpstreamValues: updateLsuvsAfterEntitiesRefresh(
        originalRow,
        liveRow,
        resolvedColumns,
        computedTable[rowIndex]
      ),
    };
  });

  return {
    ...structuredTable,
    rows: normalizeMergeCellRanges(rowsAfterCellPolicy, liveRows),
  };
}
